import { Injectable } from '@angular/core'
import { NgxPermissionsService, NgxRolesService } from 'ngx-permissions'
import { SharedModule } from '@tv3/shared/shared.module'
import { ActionDialogComponent, ActionDialogInput, ActionDialogItem } from './action-dialog.component'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'

export type GetActionParams<T extends string = string> = Record<T, Partial<ActionDialogItem>>
export type GetAddEventActionParams = GetActionParams<'addBooking' | 'addHoldEvent'>
export type GetAddRateActionParams = GetActionParams<'addStandardRate' | 'addDynamicRate'>

@Injectable({
  providedIn: SharedModule,
})
export class ActionDialogService {
  constructor(
    private dialog: MatDialog,
    private ngxPermissionsService: NgxPermissionsService,
    private ngxRolesService: NgxRolesService
  ) {}

  open(actions: ActionDialogItem[]) {
    actions = actions.filter((action) => action.visible !== false)

    if (!actions.length) return

    const defaultConfig: MatDialogConfig<ActionDialogInput> = {
      width: '350px',
      height: 'auto',
      panelClass: ['fixed-modal'],
      data: { actions, autoClose: true },
    }

    return this.dialog.open<ActionDialogComponent, ActionDialogInput, ActionDialogItem>(
      ActionDialogComponent,
      defaultConfig
    )
  }

  getAddEventActions(options: GetAddEventActionParams) {
    const actions: ActionDialogItem[] = []

    if (this.ngxPermissionsService.getPermission('canAddInquiry') && !this.ngxRolesService.getRole('OWNER')) {
      actions.push({
        text: 'Add Booking',
        icon: 'fal fa-bed',
        class: 'btn-success',
        ...options.addBooking,
      })
    }

    if (this.ngxPermissionsService.getPermission('canAddHold')) {
      actions.push({
        text: 'Add Hold Event',
        icon: 'fal fa-bookmark',
        class: 'btn-success',
        ...options.addHoldEvent,
      })
    }

    return actions
  }

  openAddEventActions(options: GetAddEventActionParams) {
    return this.open(this.getAddEventActions(options))
  }

  getAddRateActions(options: GetAddRateActionParams, isGroupRate = false) {
    const textRate = isGroupRate ? 'Group Rate' : 'Rate'
    const textRates = isGroupRate ? 'group rates' : 'rates'

    const actions: ActionDialogItem[] = [
      {
        text: `Add Standard ${textRate}`,
        class: 'btn-success',
        tooltip: `Standard ${textRates} are flat, constant rates which do not change over time.`,
        ...options.addStandardRate,
      },
      {
        text: `Add Dynamic ${textRate}`,
        class: 'btn-success',
        // tslint:disable-next-line max-line-length
        tooltip: `Dynamic ${textRates} will gradually and automatically adjust from a low rate to a high rate as your overall rental occupancy level changes. They can also instantly update your channels as the rate changes.`,
        ...options.addDynamicRate,
      },
    ]

    return actions
  }

  openAddRateActions(options: GetAddRateActionParams, isGroupRate = false) {
    return this.open(this.getAddRateActions(options, isGroupRate))
  }

  openRentalCalendarActions(options: GetAddEventActionParams & GetAddRateActionParams) {
    const actions = [...this.getAddEventActions(options), ...this.getAddRateActions(options, false)]

    return this.open(actions)
  }
}
